import { useState, useEffect, useContext } from 'react';

import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';

import {
  IconButton,
  InputAdornment,
  useTheme,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from '@mui/material';

import { Colorize } from '@mui/icons-material';

import { HexColorPicker } from 'react-colorful';

import { i18n } from '../../translate/i18n';

import api from '../../services/api';
import toastError from '../../toasts/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';

import { getStyled } from './styles';

const TagSchema = Yup.object().shape({
  name: Yup.string().min(3, 'Mensagem muito curta').required('Obrigatório'),
});

const TagModal = ({ open, onClose, tagId, kanban }) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const { user } = useContext(AuthContext);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [selectedLane, setSelectedLane] = useState([]);
  const [selectedRollbackLane, setSelectedRollbackLane] = useState([]);

  const initialState = {
    name: '',
    color: getRandomHexColor(),
  };

  const [tag, setTag] = useState(initialState);

  useEffect(() => {
    try {
      (async () => {
        if (!tagId) return;

        const { data } = await api.get(`/tags/${tagId}`);
        setTag((prevState) => {
          return { ...prevState, ...data };
        });
        if (data.nextLaneId) {
          setSelectedLane(data.nextLaneId);
        }
        if (data.rollbackLaneId) {
          setSelectedRollbackLane(data.rollbackLaneId);
        }
      })();
    } catch (err) {
      toastError(err);
    }
  }, [tagId, open]);

  const handleClose = () => {
    setTag(initialState);
    setColorPickerModalOpen(false);
    onClose();
  };

  const handleSaveTag = async (values) => {
    const tagData = {
      ...values,
      userId: user?.id,
      kanban: kanban,
      nextLaneId: selectedLane || null,
      rollbackLaneId: selectedRollbackLane || null,
    };

    try {
      if (tagId) {
        await api.put(`/tags/${tagId}`, tagData);
      } else {
        await api.post('/tags', tagData);
      }
      toastSuccess(
        kanban === 0
          ? `${i18n.t('tagModal.success')}`
          : `${i18n.t('tagModal.successKanban')}`,
      );
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  function getRandomHexColor() {
    const red = Math.floor(Math.random() * 256);
    const green = Math.floor(Math.random() * 256);
    const blue = Math.floor(Math.random() * 256);

    const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;

    return hexColor;
  }

  return (
    <div style={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        scroll='paper'
      >
        <DialogTitle id='form-dialog-title' sx={theme.custom.dialogColor}>
          {tagId
            ? kanban === 0
              ? `${i18n.t('tagModal.title.edit')}`
              : `${i18n.t('tagModal.title.editKanban')}`
            : kanban === 0
              ? `${i18n.t('tagModal.title.add')}`
              : `${i18n.t('tagModal.title.addKanban')}`}
        </DialogTitle>
        <Formik
          initialValues={tag}
          enableReinitialize={true}
          validationSchema={TagSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveTag(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers sx={theme.custom.dialogColor}>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <Field
                      as={TextField}
                      label={i18n.t('tagModal.form.name')}
                      name='name'
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant='outlined'
                      margin='dense'
                      onChange={(e) =>
                        setTag((prev) => ({ ...prev, name: e.target.value }))
                      }
                      fullWidth
                      sx={theme.custom.textField}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <Field
                      as={TextField}
                      fullWidth
                      label={i18n.t('tagModal.form.color')}
                      name='color'
                      autoFocus
                      id='color'
                      error={touched.color && Boolean(errors.color)}
                      helperText={touched.color && errors.color}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <div
                              style={{
                                ...classes.colorAdorment,
                                backgroundColor: values.color,
                              }}
                            ></div>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size='small'
                            color='default'
                            onClick={() =>
                              setColorPickerModalOpen(!colorPickerModalOpen)
                            }
                          >
                            <Colorize
                              sx={{ color: theme.custom.colors.text }}
                            />
                          </IconButton>
                        ),
                      }}
                      variant='outlined'
                      margin='dense'
                      sx={theme.custom.textField}
                    />

                    {colorPickerModalOpen && (
                      <div
                        style={{
                          margin: '20px auto 0',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <HexColorPicker
                          color={tag.color}
                          onChange={(newColor) => {
                            setTag((prev) => ({
                              ...prev,
                              color: newColor,
                            }));
                          }}
                        />
                      </div>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={theme.custom.dialogColor}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  variant='outlined'
                  sx={theme.custom.cancelButton}
                >
                  {i18n.t('tagModal.buttons.cancel')}
                </Button>
                <Button
                  type='submit'
                  disabled={isSubmitting}
                  variant='outlined'
                  sx={theme.custom.confirmButton}
                >
                  {tagId
                    ? `${i18n.t('tagModal.buttons.okEdit')}`
                    : `${i18n.t('tagModal.buttons.okAdd')}`}
                  {isSubmitting && (
                    <CircularProgress size={24} sx={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default TagModal;
